import { useState, useEffect, useCallback } from 'react';
import { Check, X, Filter, RefreshCw, TrendingUp, Loader2 } from 'lucide-react';
import { apiCall } from './api';
import { toast } from 'sonner';

/**
 * Admin : liste des paiements + validation manuelle + stats revenus.
 */
export default function AdminPayments({ token }) {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('pending');
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    try {
      const q = filter ? `?status=${filter}` : '';
      const [p, s] = await Promise.all([
        apiCall(`/admin/payments${q}`, 'GET', null, token),
        apiCall('/admin/revenue-stats', 'GET', null, token),
      ]);
      setItems(p); setStats(s);
    } catch (e) { toast.error(e.message); }
  }, [token, filter]);
  useEffect(() => { load(); }, [load]);

  const handle = async (id, action) => {
    setBusy(prev => ({ ...prev, [id]: true }));
    try {
      await apiCall('/admin/payments/validate', 'POST', { payment_id: id, action, admin_note: '' }, token);
      toast.success(action === 'validate' ? '✓ Paiement validé — accès débloqué' : 'Paiement rejeté');
      load();
    } catch (e) { toast.error(e.message); }
    setBusy(prev => ({ ...prev, [id]: false }));
  };

  return (
    <div data-testid="admin-payments">
      <h3 className="font-bold text-lg mb-2 flex items-center gap-2"><TrendingUp size={18} className="text-yellow-400" />Paiements & Revenus</h3>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          <Stat label="USD validés" value={`$${stats.by_currency.USD?.toFixed(2) || '0.00'}`} />
          <Stat label="CDF validés" value={`${(stats.by_currency.CDF || 0).toLocaleString('fr-FR')} F`} />
          <Stat label="Validés" value={stats.validated_count} />
          <Stat label="En attente" value={stats.pending_count} pulse={stats.pending_count > 0} />
        </div>
      )}

      <div className="flex items-center gap-2 mb-3">
        <Filter size={14} className="text-white/40" />
        {[
          { v: 'pending', label: 'En attente' },
          { v: 'validated', label: 'Validés' },
          { v: 'rejected', label: 'Rejetés' },
          { v: '', label: 'Tous' },
        ].map(o => (
          <button key={o.v} onClick={() => setFilter(o.v)} data-testid={`filter-${o.v || 'all'}`}
            className={`px-3 py-1 rounded-full text-xs ${filter === o.v ? 'bg-emerald-500 text-slate-950 font-bold' : 'bg-white/5 hover:bg-white/10'}`}>
            {o.label}
          </button>
        ))}
        <button onClick={load} className="ml-auto text-white/60 hover:text-white"><RefreshCw size={14} /></button>
      </div>

      <div className="space-y-2">
        {items.length === 0 && <div className="text-center text-white/50 py-8 text-sm">Aucun paiement.</div>}
        {items.map(p => (
          <div key={p.id} data-testid={`pay-${p.id}`}
            className={`rounded-xl p-3 border ${p.status === 'pending' ? 'bg-amber-500/5 border-amber-400/30' : p.status === 'validated' ? 'bg-emerald-500/5 border-emerald-400/30' : 'bg-red-500/5 border-red-400/30'}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="font-bold text-sm">{p.user_name || p.user_email}</div>
                <div className="text-xs text-white/60">{p.user_email}</div>
                <div className="text-xs text-white/50 mt-1">
                  Module : <span className="text-emerald-300">{p.module_id}</span> · {p.method.toUpperCase()} · ref <span className="font-mono">{p.transaction_ref}</span>
                </div>
                {p.note && <div className="text-xs text-white/50 italic mt-1">"{p.note}"</div>}
                <div className="text-[11px] text-white/40 mt-1">{new Date(p.submitted_at).toLocaleString('fr-FR')}</div>
              </div>
              <div className="text-right">
                <div className="font-black text-lg">{p.amount} {p.currency}</div>
                {p.status === 'pending' ? (
                  <div className="flex gap-1 mt-2">
                    <button onClick={() => handle(p.id, 'validate')} disabled={busy[p.id]} data-testid={`validate-${p.id}`}
                      className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold p-1.5 rounded-lg" title="Valider"><Check size={14} /></button>
                    <button onClick={() => handle(p.id, 'reject')} disabled={busy[p.id]}
                      className="bg-red-500 hover:bg-red-400 text-white p-1.5 rounded-lg" title="Rejeter"><X size={14} /></button>
                  </div>
                ) : (
                  <span className={`text-xs font-bold ${p.status === 'validated' ? 'text-emerald-300' : 'text-red-300'}`}>
                    {p.status === 'validated' ? '✓ Validé' : '✕ Rejeté'}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value, pulse }) {
  return (
    <div className={`bg-white/5 border border-white/10 rounded-xl p-3 ${pulse ? 'animate-pulse border-amber-400/40' : ''}`}>
      <div className="text-xs text-white/50">{label}</div>
      <div className="font-bold text-base text-white">{value}</div>
    </div>
  );
}
